import ComboBox, {
  ComboBox$SelectionChangeEventParameters,
} from "sap/m/ComboBox";
import { MetadataOptions } from "sap/ui/base/ManagedObject";
import {
  ApplyPostControlFilterAsync,
  CustomComboBoxMetadata,
  CustomComboBoxUpdateItems,
} from "../utils/CustomComboBoxUtil";
import { BetterComboBoxItemFilterFunction } from "../utils/ComboBoxFilterUtil";
import { debounceAsync } from "../utils/DebounceUtil";

/**
 * @namespace akademika.akamont.customControls
 */
export default class CustomComboBox extends ComboBox {
  static readonly metadata: MetadataOptions = CustomComboBoxMetadata;

  static renderer = { apiVersion: 2 };

  /**
   * This parameter is necessary to make sure that the fireSelectionChange event is not called when user is typing
   */
  private _isTyping = false;

  init() {
    super.init.apply(this);
    this.setFilterFunction(BetterComboBoxItemFilterFunction);
  }

  updateItems(sReason: string) {
    // @ts-expect-error Method "updateItems" exists, but isn't exposed through the type
    super.updateItems.apply(this, [sReason]);
    CustomComboBoxUpdateItems(this, sReason);
  }

  /**
   * Overriding was necessary to make updates to the list by calling the backend on every key stroke
   * @param oEvent
   */
  public async oninput(oEvent: unknown) {
    await this.debouncedApplyPostControlFilterAsync();

    if (this.getValue()) {
      this._isTyping = true;
    } else {
      this._isTyping = false;
      // The following 4 liens are necessary to update the list when the whole selection is deleted. Ex. Ctrl+A + Delete
      this.updateItems("change");
      this.setValue("");
      this.setSelectedItem(null);
      this.fireSelectionChange({ selectedItem: undefined });
    }

    // @ts-expect-error Method "ComboBox.oninput" exists, but isn't exposed through the type
    super.oninput(oEvent);
  }

  // overriding this method ensures that on binding the data, we also fetch the correct values
  // @ts-expect-error Method "ComboBox.change" exists, but isn't exposed through the type
  public async setSelectedKey(sKey: string) {
    this._isTyping = false;
    super.setSelectedKey(sKey);

    if (!sKey) return;

    const hasItemLocally = !!this.getItemByKey(sKey);
    const isListEmpty = this.getItems().length === 0;

    if (!hasItemLocally && isListEmpty) {
      await this.debouncedApplyPostControlFilterAsync(true);
    }
  }

  // @ts-expect-error Method "ComboBox.fireSelectionChange" exists, but isn't exposed through the type
  public async fireSelectionChange(
    mParameters?: ComboBox$SelectionChangeEventParameters
  ) {
    if (this._isTyping) return;
    await this.debouncedApplyPostControlFilterAsync(true);
    super.fireSelectionChange(mParameters);
  }

  public onSelectionChange(oEvent: unknown) {
    this._isTyping = false;
    // @ts-expect-error Method "ComboBox.oninput" exists, but isn't exposed through the type
    super.onSelectionChange(oEvent);
  }

  /**
   * overriding this function ensures that fireSelectionChange is called when enter is pressed
   * @param oEvent
   */
  public onsapenter(oEvent: any) {
    // @ts-expect-error Method "ComboBox.oninput" exists, but isn't exposed through the type
    super.onsapenter(oEvent);

    this._isTyping = false;
    const item = this.getSelectedItem();
    if (item) {
      this.fireSelectionChange({ selectedItem: item });
    }
  }

  /**
   * We override this so that the "x" button for clearing the text (and selected value)
   * updates the items as well.
   */
  public clearSelection(): void {
    super.clearSelection();
    this.updateItems("change");
  }

  private debouncedApplyPostControlFilterAsync = debounceAsync(
    this.applyPostControlFilterAsync.bind(this),
    200
  );

  private async applyPostControlFilterAsync(ignoreText = false) {
    await ApplyPostControlFilterAsync(
      this,
      this.getValue(),
      [this.getSelectedKey()],
      ignoreText
    );
  }
}
